export interface StorefrontProduct {
    id: number;
    name: string;
    slug: string;
    description: string;
    category: string;
    image_url: string | null;
    permalink: string | null;
    price_cents: number;
    member_price_cents: number | null;
    currency: string;
    is_featured: boolean;
}

/**
 * Cart lines are a discriminated union so the same cart UI can hold:
 *   - real `storefront_product` rows (the existing shop flow),
 *   - synthetic Custom Lab Package bundles (BioMatch v2 hero), and
 *   - synthetic Curated Recommendation enrollments (BioMatch curated cards).
 *
 * Synthetic lines are NOT backed by a `storefront_product` row — they
 * carry enough metadata for the BE to re-validate at checkout. Each
 * line's `key` namespace prevents collision with numeric product IDs.
 */
export type CartLineKind = 'product' | 'custom_kit' | 'curated_rec' | 'membership';

interface CartLineBase {
    key: string;
    kind: CartLineKind;
    name: string;
    description?: string;
    category?: string;
    image_url: string | null;
    price_cents: number;
    /** When set, this line bills on the named cadence (subscription). */
    recurring?: 'monthly' | 'annual';
    /**
     * My Labs Plus member price for this line, snapshotted at add-to-cart
     * time (e.g. `applyMemberDiscount(regularCents, orgDiscountPct)`).
     * The cart charges this instead of the regular amount when the
     * patient is a member. `null`/absent → no member discount applies.
     */
    member_price_cents?: number | null;
}

export interface ProductLine extends CartLineBase {
    kind: 'product';
    product: StorefrontProduct;
}

export interface CustomKitLine extends CartLineBase {
    kind: 'custom_kit';
    mode: 'one_time' | 'member';
    assay_codes: string[];
    product_ids: number[];
}

export interface CuratedRecLine extends CartLineBase {
    kind: 'curated_rec';
    recommendation_id: number;
    /** Patient amount after employer cost-split — what the cart charges. */
    patient_amount_cents: number;
    /** Total kit cost (informational; may differ from price_cents). */
    kit_price_cents: number;
}

export interface MembershipLine extends CartLineBase {
    kind: 'membership';
    plan: 'monthly' | 'annual';
}

export type CartLine = ProductLine | CustomKitLine | CuratedRecLine | MembershipLine;

export interface CartItem {
    line: CartLine;
    /** Synthetic lines are always quantity 1 — the cart enforces it. */
    quantity: number;
}

export type CartRecurringCadence = 'monthly' | 'annual' | 'mixed';

export function getCartRecurringCadence(items: CartItem[]): CartRecurringCadence | null {
    const cadences = new Set(
        items.flatMap(({line}) => (line.recurring ? [line.recurring] : [])),
    );

    if (cadences.size === 0) return null;
    if (cadences.size > 1) return 'mixed';
    return cadences.has('annual') ? 'annual' : 'monthly';
}

export interface ProductListResponse {
    products: StorefrontProduct[];
    total: number;
    categories: string[];
}

export const productLineKey = (productId: number): string => `product:${productId}`;
export const customKitLineKey = (mode: 'one_time' | 'member'): string => `custom_kit:${mode}`;
export const curatedRecLineKey = (recId: number): string => `curated:${recId}`;
export const membershipLineKey = (plan: MembershipLine['plan']): string => `membership:${plan}`;
